import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const CommentFieldsConfig = () => {
  const [commentFields, setCommentFields] = useState([]);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [loading, setLoading] = useState(true);

  const fieldSchema = Yup.object().shape({
    field_name: Yup.string().required('Field name is required'),
    field_code: Yup.string()
      .required('Field code is required')
      .matches(/^[a-z0-9_]+$/, 'Only lowercase letters, numbers, and underscores allowed'),
    display_name: Yup.string().required('Display name is required'),
    field_type: Yup.string().required('Field type is required'),
    max_score: Yup.number()
      .required('Max score is required')
      .min(1, 'Must be at least 1')
      .max(10, 'Cannot exceed 10'),
    min_score: Yup.number()
      .required('Min score is required')
      .min(0, 'Must be at least 0')
      .max(Yup.ref('max_score'), 'Cannot exceed max score'),
    display_order: Yup.number().required('Display order is required'),
  });

  useEffect(() => {
    fetchCommentFields();
  }, []);

  const fetchCommentFields = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCommentFields();
      setCommentFields(response.data || []);
    } catch (error) {
      toast.error('Failed to load comment fields');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveField = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editingField) {
        await adminAPI.updateCommentField(editingField.id, values);
        toast.success('Field updated successfully');
      } else {
        await adminAPI.createCommentField(values);
        toast.success('Field created successfully');
      }
      
      setShowFieldModal(false);
      resetForm();
      setEditingField(null);
      fetchCommentFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteField = async (id) => {
    if (window.confirm('Are you sure you want to delete this field?')) {
      try {
        await adminAPI.deleteCommentField(id);
        toast.success('Field deleted successfully');
        fetchCommentFields();
      } catch (error) {
        toast.error('Failed to delete field');
      }
    }
  };

  const getFieldTypeBadge = (type) => {
    const colors = {
      psychomotor: 'bg-blue-100 text-blue-800',
      affective: 'bg-green-100 text-green-800',
      custom: 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Comment Fields Configuration</h1>
          <p className="text-gray-600">Configure dynamic comment and rating fields for form masters</p>
        </div>
        <button
          onClick={() => {
            setEditingField(null);
            setShowFieldModal(true);
          }}
          className="btn-primary flex items-center"
        >
          <i className="fas fa-plus mr-2"></i> Add Field
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Fields</p>
              <p className="text-2xl font-bold">{commentFields.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <i className="fas fa-comment text-blue-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Psychomotor Fields</p>
              <p className="text-2xl font-bold">
                {commentFields.filter(f => f.field_type === 'psychomotor').length}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <i className="fas fa-running text-blue-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Affective Fields</p>
              <p className="text-2xl font-bold">
                {commentFields.filter(f => f.field_type === 'affective').length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <i className="fas fa-heart text-green-600 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-800">Comment Fields</h3>
          <p className="text-gray-600 text-sm">Manage dynamic rating fields for student comments</p>
        </div>
        
        <div className="table-responsive">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Field
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type & Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scores & Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {commentFields.map((field) => (
                <tr key={field.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {field.display_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {field.field_name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getFieldTypeBadge(field.field_type)}`}>
                        {field.field_type}
                      </span>
                      <div className="text-sm text-gray-600 font-mono">
                        {field.field_code}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="text-gray-500">Score Range: </span>
                        <span className="font-medium">
                          {field.min_score} - {field.max_score}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Display Order: </span>
                        <span className="font-medium">{field.display_order}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      field.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {field.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium space-x-2">
                    <button
                      onClick={() => {
                        setEditingField(field);
                        setShowFieldModal(true);
                      }}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteField(field.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={showFieldModal}
        onClose={() => {
          setShowFieldModal(false);
          setEditingField(null);
        }}
        title={editingField ? 'Edit Comment Field' : 'Create Comment Field'}
        size="lg"
      >
        <Formik
          initialValues={{
            field_name: editingField?.field_name || '',
            field_code: editingField?.field_code || '',
            display_name: editingField?.display_name || '',
            field_type: editingField?.field_type || 'custom',
            max_score: editingField?.max_score || 5,
            min_score: editingField?.min_score || 1,
            display_order: editingField?.display_order || 0,
            is_active: editingField?.is_active ?? true,
          }}
          validationSchema={fieldSchema}
          onSubmit={handleSaveField}
        >
          {({ isSubmitting, errors, touched, values }) => (
            <Form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Name *
                  </label>
                  <Field
                    type="text"
                    name="field_name"
                    className="input-field"
                    placeholder="e.g., Attendance Rating"
                  />
                  {errors.field_name && touched.field_name && (
                    <div className="text-red-500 text-sm mt-1">{errors.field_name}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Code *
                  </label>
                  <Field
                    type="text"
                    name="field_code"
                    className="input-field font-mono"
                    placeholder="e.g., attendance"
                  />
                  {errors.field_code && touched.field_code && (
                    <div className="text-red-500 text-sm mt-1">{errors.field_code}</div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Lowercase letters, numbers, and underscores only
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name *
                </label>
                <Field
                  type="text"
                  name="display_name"
                  className="input-field"
                  placeholder="e.g., Attendance"
                />
                {errors.display_name && touched.display_name && (
                  <div className="text-red-500 text-sm mt-1">{errors.display_name}</div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Type *
                  </label>
                  <Field as="select" name="field_type" className="input-field">
                    <option value="psychomotor">Psychomotor Domain</option>
                    <option value="affective">Affective Domain</option>
                    <option value="custom">Custom Field</option>
                  </Field>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order *
                  </label>
                  <Field
                    type="number"
                    name="display_order"
                    className="input-field"
                    min="0"
                  />
                  {errors.display_order && touched.display_order && (
                    <div className="text-red-500 text-sm mt-1">{errors.display_order}</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Score *
                  </label>
                  <Field
                    type="number"
                    name="min_score"
                    className="input-field"
                    min="0"
                    max={values.max_score}
                  />
                  {errors.min_score && touched.min_score && (
                    <div className="text-red-500 text-sm mt-1">{errors.min_score}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Score *
                  </label>
                  <Field
                    type="number"
                    name="max_score"
                    className="input-field"
                    min={values.min_score}
                    max="10"
                  />
                  {errors.max_score && touched.max_score && (
                    <div className="text-red-500 text-sm mt-1">{errors.max_score}</div>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <Field
                  type="checkbox"
                  name="is_active"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  Active Field
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowFieldModal(false);
                    setEditingField(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      {editingField ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default CommentFieldsConfig;